import { FC, useCallback } from 'react';

import { SPACE } from 'utils/constants/common';

import cpcFlexibleAgeFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_age_form_field_touch';
import cpcFlexibleAgeHideButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/cpc_flexible_age_hide_button_click';
import { Cell, CellText, Checkbox, FieldsGroupHelper, Input, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import useIsNewCampaign from 'utils/campaign/hooks/useIsNewCampaign';

import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';
import { setUnknownAgeWithSources } from 'components/CpcFlexibleCreate/CpcForm/AdAge/setUnknownAgeWithSources';

import { setCampaignAge } from 'store/models/createCampaign';
import { selectMainMenuVacancyId } from 'store/models/mainMenu';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CpcFlexibleCreate/CpcForm/AdAge/AdAge.module.less';

const FROM_INPUT = 'from';

const TrlKeys = {
    from: 'AdCreate.AdForm.AdAge.fromMagritte',
    to: 'AdCreate.AdForm.AdAge.toMagritte',
    unknown: 'AdCreate.AdForm.AdAge.unknown',
    legend: 'AdCreate.AdForm.AdAge.legend',
    hint: 'AdCreate.AdForm.AdAge.hintMagritte',
};

export const AdAge: FC = () => {
    const vacancyId = useSelector(selectMainMenuVacancyId);
    const { adAgeFrom, adAgeTo, adUnknownAge, campaignErrors } = useSelector((state) => state.createCampaign);
    const isCampaignProcessCreate = useIsNewCampaign();

    const dispatch = useDispatch();
    const trl = useTrls();

    const sendCpcFlexibleAgeFormFieldTouch = () => {
        if (isCampaignProcessCreate) {
            cpcFlexibleAgeFormFieldTouch({ vacancyId });
        }
    };

    const setUnknownAge = useCallback(() => {
        if (isCampaignProcessCreate) {
            cpcFlexibleAgeHideButtonClick({ newCheckboxState: !adUnknownAge, vacancyId });
        }
        dispatch(setUnknownAgeWithSources(adUnknownAge));
    }, [adUnknownAge, dispatch, isCampaignProcessCreate, vacancyId]);

    const onChange = (value: string, key: string) => {
        const [, textValue] = value.split(SPACE);
        const numValue = isNaN(Number(textValue)) ? 0 : Number(textValue);
        const newAges = key === FROM_INPUT ? [numValue, adAgeTo] : [adAgeFrom, numValue];
        dispatch(setCampaignAge({ adAgeFrom: newAges[0], adAgeTo: newAges[1] }));
    };

    return (
        <MagritteFormField
            input={
                <div>
                    <FieldsGroupHelper description={trl(TrlKeys.hint)}>
                        <div className={styles.inputs}>
                            <Input
                                value={`${trl(TrlKeys.from)}${SPACE}${adAgeFrom}`}
                                onChange={(value) => onChange(value, 'from')}
                                onFocus={sendCpcFlexibleAgeFormFieldTouch}
                                invalid={Boolean(campaignErrors.adAgeFrom)}
                                errorMessage={campaignErrors.adAgeFrom}
                                data-qa="age-from-input"
                            />
                            <Input
                                value={`${trl(TrlKeys.to)}${SPACE}${adAgeTo}`}
                                onChange={(value) => onChange(value, 'to')}
                                onFocus={sendCpcFlexibleAgeFormFieldTouch}
                                invalid={Boolean(campaignErrors.adAgeTo)}
                                errorMessage={campaignErrors.adAgeTo}
                                data-qa="age-to-input"
                            />
                        </div>
                    </FieldsGroupHelper>
                    <VSpacing default={12} />
                    <Cell
                        width="fit-content"
                        left={
                            <Checkbox checked={adUnknownAge} onChange={setUnknownAge} data-qa="unknown-age-checkbox" />
                        }
                    >
                        <CellText>{trl(TrlKeys.unknown)}</CellText>
                    </Cell>
                </div>
            }
            label={trl(TrlKeys.legend)}
            vSpacingBottom={32}
        />
    );
};
